import React from "react";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import { TrendingUp, TrendingDown, InfoOutlined } from "@mui/icons-material";
import { HtmlTooltip } from "components/HeatMap";
import { appendTooltipGlossary } from "./tooltipGlossary";

/**
 * ReportMetricCard - Displays a single metric with optional trend indicator
 *
 * @param {string} title - Metric title
 * @param {string|number} value - Main metric value
 * @param {string} subtitle - Optional subtitle/description
 * @param {number} trend - Optional trend percentage (positive or negative)
 * @param {string} trendLabel - Optional trend label (e.g., "vs last month")
 * @param {ReactNode} icon - Optional icon component
 * @param {string} color - Optional color for the value
 * @param {string} tooltipTitle - Optional tooltip title
 * @param {string} tooltipContent - Optional tooltip content/explanation
 * @param {function} onClick - Optional click handler to make card interactive
 */
const ReportMetricCard = ({
  title,
  value,
  subtitle,
  trend,
  trendLabel,
  icon,
  color,
  tooltipTitle,
  tooltipContent,
  onClick,
}) => {
  const theme = useTheme();
  const resolvedTooltipContent = appendTooltipGlossary(tooltipContent);

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        height: "100%",
        bgcolor: theme.palette.background.alt,
        border: `1px solid ${theme.palette.primary.border2}`,
        borderRadius: "12px",
        transition: "all 0.2s ease",
        cursor: onClick ? "pointer" : "default",
        "&:hover": {
          boxShadow: theme.shadows[4],
          transform: "translateY(-2px)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        {icon && (
          <Box
            sx={{
              color: color || theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
            }}
          >
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {title}
            </Typography>
            {resolvedTooltipContent && (
              <HtmlTooltip
                title={
                  <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto" }}>
                    {tooltipTitle && (
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        gutterBottom
                      >
                        {tooltipTitle}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                      {resolvedTooltipContent}
                    </Typography>
                  </Box>
                }
                arrow
                placement="bottom-start"
              >
                <InfoOutlined
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    fontSize: 18,
                    color: theme.palette.primary.main,
                    cursor: "help",
                    opacity: 0.7,
                    "&:hover": {
                      opacity: 1,
                    },
                  }}
                />
              </HtmlTooltip>
            )}
          </Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: color || theme.palette.text.primary,
              mb: subtitle || trend ? 0.5 : 0,
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && trend !== null && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1 }}
            >
              {trend >= 0 ? (
                <TrendingUp sx={{ fontSize: 18, color: "success.main" }} />
              ) : (
                <TrendingDown sx={{ fontSize: 18, color: "error.main" }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: trend >= 0 ? "success.main" : "error.main",
                  fontWeight: 600,
                }}
              >
                {trend >= 0 ? "+" : ""}
                {trend}%
              </Typography>
              {trendLabel && (
                <Typography variant="caption" color="text.secondary">
                  {trendLabel}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ReportMetricCard;
